/*
 * tt-collection-search.js
 * ---------------------------------------------------------------
 * Search scoped to the current collection. Markup comes from
 * snippets/tt-collection-search.liquid ([data-tt-coll-search]).
 *
 * The product grid live-mirrors the box: type to narrow it to the
 * matching cards in THIS collection, empty it to restore the full
 * collection. No Enter required. A sticky banner shows the count.
 *
 * LAYERS WITH THE SEARCH & DISCOVERY FACETS. The title/handle index
 * that decides which products match is fetched through a render-
 * fragment section (sections/tt-collection-index.liquid) on the
 * collection URL with the page's current filter.* / sort_by params
 * attached (/collections/<handle>?section_id=tt-collection-index
 * &page=N&filter...). The server applies the facets — including
 * metafield facets that can't be replicated client-side — so search
 * only ever matches products that pass the active filters, in the
 * chosen sort order. One index is cached per facet+sort state, and
 * when Dawn's facet form re-renders the grid mid-search we re-apply
 * the live query on top of the new state instead of clearing it.
 *
 * COST CONTROL — section renders are EXPENSIVE and RATE-LIMITED per
 * visitor (bursting them once got Dawn's own facet fetch a 429, which
 * wedged the whole filter UI), so:
 *   - the PLAIN browse state (no filters, default sort) indexes via
 *     the lightweight /products.json feed — zero section renders;
 *   - filtered/sorted states use the fragment at low concurrency;
 *   - a failed page fetch retries with backoff; repeated failure
 *     closes the index out with what it has, and any fresh intent
 *     (focus/typing) re-opens it once the budget has refilled;
 *   - at idle we warm ONLY page 1; the rest loads on focus/typing;
 *   - the facet querystring is canonicalised (empty values + the
 *     default sort dropped) so logically identical states share one
 *     cached index instead of refetching;
 *   - a chain whose facet state left the screen stops requesting and
 *     resumes exactly where it left off if that state returns.
 *
 * The result cards themselves are the SAME server-rendered Liquid
 * card as the grid, fetched on demand via the Section Rendering API
 * (/products/<handle>?section_id=tt-product-card) and cached. So
 * results carry real inventory/stock-cap state, responsive images
 * and card-flip — and there is no hand-built card to drift out of
 * sync with snippets/tt-grid-card.liquid.
 */
(function () {
  /* Index the whole (filtered) collection so every card is findable. The cap
     is a runaway guard — fetching stops once paginate reports the last page.
     Must exceed the largest collection (yu-gi-oh ~6,700; pokemon ~5,500). */
  var PAGE_CAP = 40;                  /* 40 x 250 = up to 10,000 products */
  var MAX_GRID = 60;                  /* cards shown for a single query */
  var CARD_SECTION = 'tt-product-card';
  var INDEX_SECTION = 'tt-collection-index';
  /* The forced in-stock state (sold-out hiding) — still index via the free
     products.json feed and drop sold-out rows client-side, instead of paying
     ~27 section renders per collection on the big games. */
  var AVAIL_QS = 'filter.v.availability=1';
  /* Concurrency is deliberately modest: card + index fetches share one
     per-visitor section-render budget with Dawn's facet re-renders. */
  var CARD_CONCURRENCY = 4;           /* parallel card fetches in flight */
  var INDEX_CONCURRENCY = 2;          /* parallel index-page fetches in flight */
  var INDEX_RETRY_DELAYS = [1500, 4000, 8000]; /* backoff between failed-page retries */
  var caches = {};                    /* handle|facetQS -> index cache (see ensureIndex) */
  var cacheOrder = [];                /* insertion order, for pruning */
  var CACHE_MAX = 8;                  /* facet states kept per session */
  var cardCache = {};                 /* product handle -> Promise<card HTML or null> */
  var renderGen = 0;                  /* bumps each query so stale fetches don't inject */

  /* ----- grid-search state (shared across the desktop + mobile inputs) ----- */
  var collHandle = '', collTitle = '';
  var defaultSortBy = null;           /* collection's default sort, learned from #SortBy */
  var gridEl = null, countEl = null, containerEl = null, bannerEl = null;
  var savedGridHTML = null, savedCountHTML = null, savedPagDisplay = null;
  var searchQuery = '', searchRaw = '', searchTitle = '';
  var lastRenderKey = ''; /* skip redundant re-renders as the index streams in */

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function matchAll(p, terms) {
    for (var i = 0; i < terms.length; i++) {
      if (p.search.indexOf(terms[i]) === -1) return false;
    }
    return true;
  }

  /* ------------------------- facet (filter) state ------------------------- */

  /* The active facets + sort, straight from the URL — Dawn's facets.js pushes
     every filter/sort change into location.search before its grid re-render
     lands, so the URL is always current. Canonicalised (sorted; empty values
     and the default sort dropped — Dawn serialises untouched price boxes as
     `filter.v.price.gte=` and always appends sort_by, which would otherwise
     make identical states miss the cache) so the same selection always
     produces the same string; doubles as the cache key. */
  function currentFacetQS() {
    var pairs = [];
    new URLSearchParams(location.search).forEach(function (value, key) {
      if (value === '') return;
      if (key === 'sort_by' && value === defaultSortBy) return;
      if (key.indexOf('filter.') === 0 || key === 'sort_by') pairs.push([key, value]);
    });
    pairs.sort(function (a, b) {
      a = a.join('='); b = b.join('=');
      return a < b ? -1 : a > b ? 1 : 0;
    });
    return pairs.map(function (p) {
      return encodeURIComponent(p[0]) + '=' + encodeURIComponent(p[1]);
    }).join('&');
  }

  function hasActiveFilters() {
    var found = false;
    new URLSearchParams(location.search).forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && value !== '') found = true;
    });
    return found;
  }

  function currentKey() { return collHandle + '|' + currentFacetQS(); }

  /* ------------------------------- index -------------------------------- */

  /* One page of the title/handle index, server-filtered by the facet params.
     sections/tt-collection-index.liquid emits the JSON payload. */
  function fetchIndexPage(qs, page) {
    var url = '/collections/' + encodeURIComponent(collHandle) +
      '?section_id=' + INDEX_SECTION + '&page=' + page + (qs ? '&' + qs : '');
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var payload = tmp.querySelector('[data-tt-coll-index]');
        if (!payload) throw new Error('no index payload');
        return JSON.parse(payload.textContent);
      });
  }

  /* Get (or start building) the index for the CURRENT facet+sort state.
     Pages stream to subscribers so partial results fill in while the rest
     loads. Cached per facet state — toggling back to an already-seen filter
     combination reuses (or resumes) its index without refetching.
     limitPages caps how deep THIS call fetches: the idle warm passes 1 so a
     page view costs a single request; focus/typing pass nothing (= full). */
  function ensureIndex(limitPages) {
    var key = currentKey();
    var cache = caches[key];
    if (!cache) {
      var qs = currentFacetQS();
      cache = caches[key] = {
        qs: qs,
        /* The plain browse state indexes via the lightweight /products.json
           feed, costing none of the per-visitor section-render budget that
           Dawn's facet fetches and our card fetches share. Only states the
           server must filter/sort use the tt-collection-index fragment. */
        useJson: qs === '' || qs === AVAIL_QS,
        availOnly: qs === AVAIL_QS,
        products: [],   /* contiguous, in collection sort order */
        pageData: {},   /* page no. -> raw batch, until assembled */
        requested: {},  /* page no. -> true once fetched/in flight */
        inFlight: 0,
        pages: null,    /* total pages, learned from the first response */
        assembled: 1,   /* next page to fold into products */
        target: 0,      /* deepest page any caller has asked for */
        attempts: 0,    /* consecutive page-fetch failures (reset on success) */
        failed: false,  /* closed out by repeated failures — revivable */
        done: false,
        subs: []
      };
      cacheOrder.push(key);
      /* Heavy facet exploration on big collections otherwise accumulates
         10k-product indexes for the whole session. Keep the newest few. */
      while (cacheOrder.length > CACHE_MAX) {
        var oldest = cacheOrder.shift();
        if (oldest !== key) delete caches[oldest];
      }
      /* One shared subscriber per index (the desktop + mobile inputs share
         it): re-run the live filter as pages stream in, but only while this
         facet state is still the one on screen. */
      cache.subs.push(function () {
        if (searchQuery && key === currentKey()) runGridSearch();
      });
    }
    /* An index closed out by repeated fetch failures (rate limiting) gets
       another chance whenever the shopper shows fresh intent — by then the
       request budget has refilled. */
    if (cache.failed) { cache.failed = false; cache.done = false; cache.attempts = 0; }
    var want = Math.min(limitPages || PAGE_CAP, PAGE_CAP);
    if (want > cache.target) cache.target = want;
    pump(cache);
    return cache;
  }

  function notifySubs(cache) {
    cache.subs.forEach(function (fn) { try { fn(cache); } catch (e) {} });
  }

  /* One page of the plain-state index from the lightweight JSON feed (the
     same endpoint the pre-facet implementation used). qs is unused — this
     path only serves the no-filter, default-sort state. */
  function fetchJsonPage(qs, page) {
    return fetch('/collections/' + encodeURIComponent(collHandle) + '/products.json?limit=250&page=' + page)
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return { products: (data && data.products) || [], pages: null };
      });
  }

  /* Start as many page fetches as the target + concurrency cap allow. */
  function pump(cache) {
    if (cache.done) return;
    /* This facet state left the screen — stop starting requests. The next
       ensureIndex() for it resumes exactly where this left off. */
    if (cache.qs !== currentFacetQS()) return;
    /* products.json gives no total page count — walk it one page at a time
       and stop at the first short page. The section index reports `pages`
       up front, so known pages can be fetched a couple at a time. */
    var conc = cache.useJson ? 1 : INDEX_CONCURRENCY;
    var known;
    if (cache.useJson) {
      known = cache.assembled;
    } else {
      known = cache.pages === null ? 1 : Math.min(cache.pages, PAGE_CAP);
    }
    var limit = Math.min(cache.target, known);
    for (var p = 1; p <= limit; p++) {
      if (cache.inFlight >= conc) return;
      if (cache.requested[p]) continue;
      requestPage(cache, p);
    }
  }

  function requestPage(cache, page) {
    cache.requested[page] = true;
    cache.inFlight++;
    var fetchPage = cache.useJson ? fetchJsonPage : fetchIndexPage;
    fetchPage(cache.qs, page)
      .then(function (data) {
        cache.inFlight--;
        cache.attempts = 0;
        cache.pageData[page] = (data && data.products) || [];
        if (data && data.pages) cache.pages = data.pages;
        assemble(cache);
        pump(cache);
      })
      .catch(function () {
        cache.inFlight--;
        delete cache.requested[page]; /* so the retry re-requests this page */
        cache.attempts++;
        if (cache.attempts > INDEX_RETRY_DELAYS.length) {
          /* Still failing after backoff — close out with what we have rather
             than hammering a drained budget. ensureIndex revives this cache
             on the shopper's next focus/keystroke. */
          cache.failed = true;
          cache.done = true;
          notifySubs(cache);
          return;
        }
        /* Back off before retrying — an immediate retry hits the same
           drained rate-limit bucket and just burns the attempt. */
        setTimeout(function () {
          if (!cache.done) pump(cache);
        }, INDEX_RETRY_DELAYS[cache.attempts - 1]);
      });
  }

  /* Fold fetched pages into the products list IN ORDER (parallel fetches can
     land out of order; results must follow the collection sort). */
  function assemble(cache) {
    var grew = false;
    while (cache.pageData[cache.assembled]) {
      var batch = cache.pageData[cache.assembled];
      for (var i = 0; i < batch.length; i++) {
        var p = batch[i];
        /* products.json rows carry variants — drop sold-out in the forced in-stock state */
        if (cache.availOnly && p.variants) {
          var anyAvail = false;
          for (var va = 0; va < p.variants.length; va++) {
            if (p.variants[va].available) { anyAvail = true; break; }
          }
          if (!anyAvail) continue;
        }
        cache.products.push({ title: p.title, search: p.title.toLowerCase(), handle: p.handle });
      }
      grew = true;
      /* An empty page ends either mode; a short page ends the JSON feed
         (which never reports a total page count). */
      if (!batch.length || (cache.useJson && batch.length < 250)) cache.done = true;
      delete cache.pageData[cache.assembled];
      cache.assembled++;
    }
    var last = cache.pages === null ? null : Math.min(cache.pages, PAGE_CAP);
    if (last !== null && cache.assembled > last) cache.done = true;
    if (grew || cache.done) notifySubs(cache);
  }

  /* --------------------------- server cards ----------------------------- */

  function resolveGridEls() {
    gridEl = document.getElementById('product-grid');
    countEl = document.getElementById('ProductCountDesktop');
    containerEl = document.getElementById('ProductGridContainer');
  }

  /* Fetch one product's real Liquid card. The cache stores the PROMISE, not
     the result, so re-renders while a card is still in flight share the same
     request instead of fetching it twice. Resolves to the card's outerHTML,
     or null. A 200 with no card in it (product gone) caches null; a FAILED
     fetch (rate limit, network) retries once after a pause and, if still
     failing, is dropped from the cache so a later render can try again
     instead of leaving a hole for the rest of the session. */
  function fetchCard(handle) {
    if (Object.prototype.hasOwnProperty.call(cardCache, handle)) {
      return cardCache[handle];
    }
    function attempt() {
      return fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION)
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var card = tmp.querySelector('.tt-grid-card');
          return card ? card.outerHTML : null;
        });
    }
    function after(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }
    var p = attempt()
      .catch(function () { return after(1500).then(attempt); })
      .catch(function () { return after(4000).then(attempt); })
      .catch(function () {
        delete cardCache[handle]; /* so a later render retries */
        return false; /* fetch failure — distinct from null (product gone) */
      });
    cardCache[handle] = p;
    return p;
  }

  /* Run thunks with a bounded number in flight; resolves when all settle. */
  function runConcurrent(thunks, limit) {
    var i = 0;
    function worker() {
      if (i >= thunks.length) return;
      var idx = i++;
      return Promise.resolve(thunks[idx]()).then(worker);
    }
    var pool = [];
    for (var k = 0; k < Math.min(limit, thunks.length); k++) pool.push(worker());
    return Promise.all(pool);
  }

  function ensureBanner() {
    if (bannerEl) return;
    bannerEl = document.createElement('div');
    bannerEl.className = 'tt-coll-search__banner';
    containerEl.insertBefore(bannerEl, containerEl.firstChild);
    bannerEl.addEventListener('click', function (e) {
      if (e.target.closest('.tt-coll-search__clear')) clearGridSearch();
    });
  }

  function applyGridSearch(matches, done, incomplete) {
    if (!gridEl || !containerEl) return;
    /* Same facet state + query + match count + load state → nothing to redo.
       Skips the skeleton churn when an index page adds no new matches, and
       collapses the desktop+mobile double-subscription into one render. */
    var key = currentKey() + '|' + searchQuery + '|' + matches.length + '|' + (done ? '1' : '0') + (incomplete ? 'f' : '');
    if (key === lastRenderKey) return;
    lastRenderKey = key;

    /* Stash the untouched grid the first time we take it over, and pause the
       infinite-scroll loader so it stops appending unrelated pages. */
    if (savedGridHTML === null) {
      savedGridHTML = gridEl.innerHTML;
      savedCountHTML = countEl ? countEl.innerHTML : null;
      var pag0 = containerEl.querySelector('.pagination-wrapper');
      savedPagDisplay = pag0 ? pag0.style.display : null;
      if (window.ttInfiniteScroll) window.ttInfiniteScroll.pause();
    }

    ensureBanner();
    var filtered = hasActiveFilters();
    var countLabel = matches.length === 1 ? '1 result' : matches.length + ' results';
    var extra = matches.length > MAX_GRID ? ' (showing first ' + MAX_GRID + ')' : '';
    bannerEl.innerHTML =
      '<span class="tt-coll-search__banner-text">' +
        '<strong>' + countLabel + '</strong>' + extra + ' for &ldquo;' + escapeHtml(searchRaw) + '&rdquo;' +
        (searchTitle ? ' in ' + escapeHtml(searchTitle) : '') +
        (filtered ? ' &middot; filters applied' : '') +
        (incomplete ? ' &middot; may be incomplete &mdash; keep typing to retry' : (done ? '' : ' &middot; loading&hellip;')) +
      '</span>' +
      '<button type="button" class="tt-coll-search__clear">Clear &times;</button>';

    if (countEl) countEl.textContent = countLabel;
    var pag = containerEl.querySelector('.pagination-wrapper');
    if (pag) pag.style.display = 'none';

    if (!matches.length) {
      gridEl.innerHTML = '<li class="tt-coll-search__grid-empty">' +
        (done
          ? 'No cards in ' + escapeHtml(searchTitle || 'this collection') + ' match &ldquo;' + escapeHtml(searchRaw) + '&rdquo;' +
            (filtered ? ' with the current filters.' : '.')
          : 'Searching&hellip;') +
        '</li>';
      return;
    }

    /* Lay out skeletons immediately, then swap each for its real server card
       as it arrives. gen guards against a newer query landing first. */
    var gen = ++renderGen;
    var shown = matches.slice(0, MAX_GRID);
    gridEl.innerHTML = shown.map(function (p, i) {
      return '<li class="grid__item" data-tt-card-slot="' + i + '">' +
        '<div class="tt-grid-card tt-grid-card--skeleton" aria-hidden="true"></div></li>';
    }).join('');

    var failedCards = 0;
    runConcurrent(shown.map(function (p, i) {
      return function () {
        return fetchCard(p.handle).then(function (cardHtml) {
          if (gen !== renderGen || !gridEl) return;
          var slot = gridEl.querySelector('[data-tt-card-slot="' + i + '"]');
          if (!slot) return;
          if (cardHtml) slot.innerHTML = cardHtml;
          else {
            if (cardHtml === false) failedCards++;
            if (slot.parentNode) slot.parentNode.removeChild(slot);
          }
        });
      };
    }), CARD_CONCURRENCY).then(function () {
      if (gen !== renderGen || !gridEl) return;
      if (!gridEl.children.length) {
        gridEl.innerHTML = '<li class="tt-coll-search__grid-empty">Couldn’t load these cards — please refresh.</li>';
      } else if (failedCards > 0 && bannerEl) {
        /* The count above the grid includes cards whose fetch was rate-limited
           away — say so rather than quietly showing fewer cards than counted. */
        var note = bannerEl.querySelector('.tt-coll-search__banner-text');
        if (note) note.innerHTML += ' &middot; ' + failedCards +
          (failedCards === 1 ? ' card' : ' cards') + ' didn&rsquo;t load &mdash; type again to retry';
      }
    });
  }

  function clearGridSearch() {
    searchQuery = ''; searchRaw = ''; lastRenderKey = '';
    renderGen++; /* abandon any in-flight card fetches */
    /* Stop the index chain from fetching pages nobody is searching against —
       it shares the per-visitor section-render budget with Dawn's facet
       fetches, and draining it here is what made post-search filter clicks
       429. ensureIndex() raises the target again on the next focus/keystroke,
       resuming exactly where this left off. */
    var c = caches[currentKey()];
    if (c && !c.done) c.target = 0;
    if (gridEl && savedGridHTML !== null) gridEl.innerHTML = savedGridHTML;
    if (countEl && savedCountHTML !== null) countEl.innerHTML = savedCountHTML;
    savedGridHTML = null; savedCountHTML = null;
    if (containerEl) {
      var pag = containerEl.querySelector('.pagination-wrapper');
      if (pag && savedPagDisplay !== null) pag.style.display = savedPagDisplay;
    }
    savedPagDisplay = null;
    if (bannerEl) { bannerEl.remove(); bannerEl = null; }
    document.querySelectorAll('.tt-coll-search__input').forEach(function (i) { i.value = ''; });
    /* Hand the grid back to infinite scroll, exactly where it left off. */
    if (window.ttInfiniteScroll) window.ttInfiniteScroll.resume();
  }

  /* When Dawn's facet/sort form re-renders the grid server-side, it swaps a
     brand-new #product-grid (filtered/sorted result) into the container. If a
     search is live, re-apply it ON TOP of the new state: the fresh server grid
     becomes the restore point (so clearing the box returns the FILTERED grid),
     and the query re-runs against the index for the new facet params — facets
     and the quick search layer in either order. Re-asserting the OLD takeover
     over the new grid is what once made filters look ignored (out-of-stock
     cards staying visible after ticking "In stock"). */
  function reapplyOverServerGrid() {
    if (savedGridHTML === null && !searchQuery) return; /* search isn't active */
    renderGen++;                        /* abandon any in-flight card fetches */
    /* Drop the stash WITHOUT restoring it — the server grid is authoritative. */
    savedGridHTML = null; savedCountHTML = null; savedPagDisplay = null;
    if (bannerEl) { bannerEl.remove(); bannerEl = null; }
    gridEl = null; countEl = null; containerEl = null;
    lastRenderKey = '';
    if (!searchQuery) return;
    /* Defer one tick: tt-infinite-scroll's own grid-swap watcher must re-init
       on the new grid FIRST (it clears its paused state), so our takeover can
       pause it again before it appends page 2 of the filtered results. */
    setTimeout(function () {
      if (!searchQuery) return;
      ensureIndex();
      runGridSearch();
    }, 0);
  }

  /* Detect Dawn's server re-render (a fresh #product-grid node appears) and
     layer the live search back over it. Mirrors tt-infinite-scroll's grid-swap
     watcher. Our own takeover only mutates #product-grid's children (same
     element), so this never mis-fires on the search's own rendering. */
  function watchForServerRerender() {
    var mo = new MutationObserver(function (mutations) {
      if (savedGridHTML === null && !searchQuery) return; /* search not active */
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.id === 'product-grid' || (node.querySelector && node.querySelector('#product-grid'))) {
            reapplyOverServerGrid();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function runGridSearch() {
    resolveGridEls();
    if (!gridEl || !containerEl || !searchQuery) return;
    var terms = searchQuery.split(/\s+/);
    var cache = caches[currentKey()] || ensureIndex();
    var matches = cache.products.filter(function (p) { return matchAll(p, terms); });
    applyGridSearch(matches, cache.done, cache.failed);
  }

  /* ------------------------------- wiring -------------------------------- */

  function init(root) {
    var handle = root.getAttribute('data-tt-coll-search');
    var title = root.getAttribute('data-tt-coll-title') || '';
    var input = root.querySelector('.tt-coll-search__input');
    if (!handle || !input) return;
    collHandle = handle;
    collTitle = title;

    var debounceTimer = null;

    /* The grid always mirrors the box: type to narrow, empty to restore.
       No Enter required — searching a different card just means typing it. */
    function applyFromInput() {
      var raw = input.value.trim();
      if (!raw) { clearGridSearch(); return; }
      searchRaw = raw;
      searchQuery = raw.toLowerCase();
      searchTitle = collTitle;
      ensureIndex();
      runGridSearch();
    }

    input.addEventListener('focus', function () { ensureIndex(); });

    /* This input sits inside Dawn's FacetSortForm, which auto-submits on ANY
       'input'/'change' event (facets.js) and would re-fetch the UNFILTERED
       collection over our results on every keystroke — the grid flashes the
       matches, then reverts to the full list. Stop our events from reaching
       that form listener. The Sort <select> is unaffected (its own events
       still bubble), so sorting keeps working. */
    input.addEventListener('change', function (e) { e.stopPropagation(); });

    input.addEventListener('input', function (e) {
      e.stopPropagation();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyFromInput, 180);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        /* Don't submit the facet sort form; just apply immediately. */
        e.preventDefault();
        clearTimeout(debounceTimer);
        applyFromInput();
        input.blur();
      } else if (e.key === 'Escape') {
        clearTimeout(debounceTimer);
        input.value = '';
        clearGridSearch();
        input.blur();
      }
    });

    /* Warm ONLY the first index page after first paint, so the first
       keystroke has data to filter without the page view paying for the
       whole chain (up to ~27 expensive section renders on the biggest
       collections). The rest loads on focus/typing — real intent. */
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { ensureIndex(1); }, { timeout: 2500 });
    } else {
      setTimeout(function () { ensureIndex(1); }, 1200);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    var roots = document.querySelectorAll('[data-tt-coll-search]');
    if (!roots.length) return;
    /* Learn the collection's default sort from the Sort select on a clean
       URL, so "no sort param" and "explicit default sort" share one index. */
    if (!new URLSearchParams(location.search).get('sort_by')) {
      var sortSel = document.getElementById('SortBy');
      if (sortSel) {
        /* Read the server-rendered selected attribute, not .value — browsers
           restore .value from form state on reload, which could mislearn. */
        var def = sortSel.querySelector('option[selected]');
        defaultSortBy = def ? def.value : sortSel.value;
      }
    }
    roots.forEach(function (root) { init(root); });
    watchForServerRerender();
  });
})();
